import React, { useState, useEffect, useRef } from 'react';
import { toast } from 'react-toastify';

const PrivateMessaging = ({ user }) => {
  const [conversations, setConversations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [members, setMembers] = useState([]);
  const [showNewModal, setShowNewModal] = useState(false);
  const [newRecipient, setNewRecipient] = useState('');
  const [newContent, setNewContent] = useState('');
  const [sending, setSending] = useState(false);
  const messagesEndRef = useRef(null);
  
  const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
  const arcaGreen = '#6B8E23';
  const arcaLightGreen = '#E8F5E9';
  
  useEffect(() => {
    if (user) {
      fetchConversations();
      fetchMembers();
    }
  }, [user]);

  const [messages, setMessages] = useState({});

  const [activeConversation, setActiveConversation] = useState(null);
  const [newMessage, setNewMessage] = useState('');
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    if (activeConversation) {
      fetchMessages(activeConversation.id);
      markAsRead(activeConversation.id);
    }
  }, [activeConversation]);
  
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);
  
  const fetchConversations = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('userToken') || localStorage.getItem('adminToken') || localStorage.getItem('token');
      const response = await fetch(`${API_BASE}/communication/messages/conversations`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      if (data.success) setConversations(data.data);
    } catch (error) {
      console.error('Error fetching conversations:', error);
      toast.error('Failed to load conversations');
    } finally {
      setLoading(false);
    }
  };
  
  const fetchMessages = async (conversationId) => {
    try {
      const token = localStorage.getItem('userToken') || localStorage.getItem('adminToken') || localStorage.getItem('token');
      const response = await fetch(`${API_BASE}/communication/messages/conversations/${conversationId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      if (data.success) {
        setMessages(prev => ({ ...prev, [conversationId]: data.data }));
      }
    } catch (error) {
      console.error('Error fetching messages:', error);
    }
  };
  
  const fetchMembers = async () => {
    try {
      const token = localStorage.getItem('userToken') || localStorage.getItem('adminToken') || localStorage.getItem('token');
      const response = await fetch(`${API_BASE}/members/search`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      if (data.success) setMembers(data.data);
    } catch (error) {
      console.error('Error fetching members:', error);
    }
  };
  
  const markAsRead = async (conversationId) => {
    try {
      const token = localStorage.getItem('userToken') || localStorage.getItem('adminToken') || localStorage.getItem('token');
      await fetch(`${API_BASE}/communication/messages/conversations/${conversationId}/read`, {
        method: 'PATCH',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setConversations(prev => prev.map(conv => 
        conv.id === conversationId ? { ...conv, unread_count: 0 } : conv
      ));
    } catch (error) {
      console.error('Error marking as read:', error);
    }
  };
  
  const getInitials = (name) => {
    if (!name) return '??';
    return name.split(' ').map(p => p[0]).join('').toUpperCase().substring(0, 2);
  };
  
  const formatTimestamp = (timestamp) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);
    
    if (diffMins < 60) return `${diffMins}min`;
    if (diffHours < 24) return `${diffHours}h`;
    if (diffDays < 7) return `${diffDays}d`;
    return `${Math.floor(diffDays / 7)}w`;
  };

  // Get active conversation details
  const activeConversationData = activeConversation;
  
  // Filter conversations based on search term
  const filteredConversations = conversations.filter(conv => 
    conv.participants?.toLowerCase().includes(searchTerm.toLowerCase())
  );
  
  if (!user) {
    return (
      <div className="d-flex align-items-center justify-content-center" style={{ minHeight: '400px' }}>
        <div className="text-center">
          <h3 className="h5 text-muted">Please log in to access private messaging</h3>
        </div>
      </div>
    );
  }

  // Handle sending a new message
  const handleSendMessage = async () => {
    if (newMessage.trim() === '' || !activeConversation) return;

    try {
      setSending(true);
      const token = localStorage.getItem('userToken') || localStorage.getItem('adminToken') || localStorage.getItem('token');
      const response = await fetch(`${API_BASE}/communication/messages`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          recipient_ids: activeConversation.participant_ids || [activeConversation.other_participant_id],
          content: newMessage
        })
      });
      
      const data = await response.json();
      if (data.success) {
        setNewMessage('');
        fetchMessages(activeConversation.id);
        fetchConversations();
      } else {
        toast.error(data.message || 'Failed to send message');
      }
    } catch (error) {
      console.error('Error sending message:', error);
      toast.error('Failed to send message');
    } finally {
      setSending(false);
    }
  };
  
  const handleStartConversation = async (e) => {
    e.preventDefault();
    if (!newRecipient || !newContent.trim()) {
      toast.error('Please select a recipient and enter a message');
      return;
    }

    try {
      setSending(true);
      const token = localStorage.getItem('userToken') || localStorage.getItem('adminToken') || localStorage.getItem('token');
      const response = await fetch(`${API_BASE}/communication/messages/conversations`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          participant_ids: [newRecipient],
          initial_message: { content: newContent }
        })
      });
      
      const data = await response.json();
      if (data.success) {
        toast.success('Message sent!');
        setShowNewModal(false);
        setNewRecipient('');
        setNewContent('');
        fetchConversations();
        setActiveConversation(data.data.conversation);
      } else {
        toast.error(data.message || 'Failed to send message');
      }
    } catch (error) {
      console.error('Error starting conversation:', error);
      toast.error('Failed to send message');
    } finally {
      setSending(false);
    }
  };

  // Handle key press for sending message on Enter
  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  return (
    <>
      {/* New Message Modal */}
      {showNewModal && (
        <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }} onClick={() => setShowNewModal(false)}>
          <div className="modal-dialog modal-dialog-centered" onClick={(e) => e.stopPropagation()}>
            <div className="modal-content border-0 shadow">
              <div className="modal-header" style={{ backgroundColor: arcaGreen }}>
                <h5 className="modal-title text-white fw-bold">
                  <i className="fas fa-envelope me-2"></i>New Message
                </h5>
                <button type="button" className="btn-close btn-close-white" onClick={() => setShowNewModal(false)}></button>
              </div>
              <div className="modal-body p-4">
                <form onSubmit={handleStartConversation}>
                  <div className="mb-3">
                    <label className="form-label fw-medium">Recipient</label>
                    <select
                      value={newRecipient}
                      onChange={(e) => setNewRecipient(e.target.value)}
                      required
                      className="form-select"
                    >
                      <option value="">Select a member...</option>
                      {members.filter(m => m.id !== user?.id).map(member => (
                        <option key={member.id} value={member.id}>
                          {member.first_name} {member.last_name} ({member.membership_type})
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="mb-3">
                    <label className="form-label fw-medium">Message</label>
                    <textarea
                      value={newContent}
                      onChange={(e) => setNewContent(e.target.value)}
                      placeholder="Type your message..."
                      rows="4"
                      required
                      className="form-control"
                    />
                  </div>
                  <div className="d-flex justify-content-end gap-2">
                    <button
                      type="button"
                      onClick={() => setShowNewModal(false)}
                      className="btn btn-secondary"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      disabled={sending}
                      className="btn text-white"
                      style={{ backgroundColor: arcaGreen }}
                    >
                      {sending ? 'Sending...' : 'Send Message'}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      )}
      
      <div className="card border-0 shadow-sm" style={{ height: '600px' }}>
        <div className="row g-0 h-100">
          {/* Conversations List */}
          <div className="col-4 border-end d-flex flex-column h-100">
            <div className="p-3 border-bottom">
              <div className="d-flex justify-content-between align-items-center mb-3">
                <h5 className="mb-0 fw-bold">Messages</h5>
                <button 
                  onClick={() => setShowNewModal(true)}
                  className="btn btn-sm text-white"
                  style={{ backgroundColor: arcaGreen }}
                >
                  <i className="fas fa-plus"></i>
                </button>
              </div>
              <div className="position-relative">
                <i className="fas fa-search position-absolute text-muted" style={{ left: '12px', top: '12px' }}></i>
                <input
                  type="text"
                  placeholder="Search conversations..."
                  className="form-control ps-5"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
            </div>
            <div className="overflow-auto flex-grow-1">
              {loading ? (
                <div className="p-4 text-center text-muted">
                  <div className="spinner-border spinner-border-sm" style={{ color: arcaGreen }} role="status">
                    <span className="visually-hidden">Loading...</span>
                  </div>
                  <p className="mt-2 small">Loading...</p>
                </div>
              ) : filteredConversations.length === 0 ? (
                <div className="p-4 text-center text-muted">
                  <i className="fas fa-comments fa-3x mb-3" style={{ color: '#dee2e6' }}></i>
                  <p className="small">No conversations yet</p>
                  <button 
                    onClick={() => setShowNewModal(true)}
                    className="btn btn-sm text-white mt-2"
                    style={{ backgroundColor: arcaGreen }}
                  >
                    Start a conversation
                  </button>
                </div>
              ) : (
                filteredConversations.map(conversation => (
                  <div 
                    key={conversation.id}
                    className={`p-3 border-bottom ${activeConversation?.id === conversation.id ? '' : ''}`}
                    style={{ 
                      cursor: 'pointer',
                      backgroundColor: activeConversation?.id === conversation.id ? arcaLightGreen : 'transparent'
                    }}
                    onClick={() => setActiveConversation(conversation)}
                    onMouseEnter={(e) => {
                      if (activeConversation?.id !== conversation.id) {
                        e.currentTarget.style.backgroundColor = '#f8f9fa';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (activeConversation?.id !== conversation.id) {
                        e.currentTarget.style.backgroundColor = 'transparent';
                      }
                    }}
                  >
                    <div className="d-flex align-items-center">
                      <div className="position-relative">
                        <div className="rounded-circle text-white d-flex align-items-center justify-content-center fw-bold" 
                          style={{ 
                            width: '48px', 
                            height: '48px', 
                            backgroundColor: arcaGreen 
                          }}>
                          {getInitials(conversation.participants)}
                        </div>
                      </div>
                      <div className="ms-3 flex-grow-1" style={{ minWidth: 0 }}>
                        <div className="d-flex justify-content-between">
                          <h6 className="mb-0 fw-medium text-truncate">{conversation.participants}</h6>
                          <span className="text-muted small">{formatTimestamp(conversation.updated_at)}</span>
                        </div>
                        <p className="text-muted small mb-0 text-truncate">{conversation.last_message}</p>
                      </div>
                      {conversation.unread_count > 0 && (
                        <span className="badge rounded-pill ms-2 text-white" style={{ backgroundColor: arcaGreen }}>
                          {conversation.unread_count}
                        </span>
                      )}
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
          
          {/* Chat Area */}
          <div className="col-8 d-flex flex-column h-100">
            {activeConversationData ? (
              <>
                <div className="p-3 border-bottom">
                  <div className="d-flex align-items-center">
                    <div className="position-relative">
                      <div className="rounded-circle text-white d-flex align-items-center justify-content-center fw-bold" 
                        style={{ 
                          width: '40px', 
                          height: '40px', 
                          backgroundColor: arcaGreen 
                        }}>
                        {getInitials(activeConversationData.participants)}
                      </div>
                    </div>
                    <div className="ms-3">
                      <h6 className="mb-0 fw-bold">{activeConversationData.participants}</h6>
                      <p className="mb-0 small text-muted">ARCA Member</p>
                    </div>
                  </div>
                </div>
                
                <div className="flex-grow-1 overflow-auto p-3" style={{ backgroundColor: '#f8f9fa' }}>
                  <div className="d-flex flex-column gap-3">
                    {(messages[activeConversationData.id] || []).map(message => {
                      const isOwn = message.sender_id === user?.id;
                      return (
                        <div 
                          key={message.id} 
                          className={`d-flex ${isOwn ? 'justify-content-end' : 'justify-content-start'}`}
                        >
                          <div 
                            className="rounded p-3"
                            style={{
                              maxWidth: '70%',
                              backgroundColor: isOwn ? arcaGreen : '#fff',
                              color: isOwn ? '#fff' : '#000',
                              borderRadius: isOwn ? '12px 12px 0 12px' : '12px 12px 12px 0'
                            }}
                          >
                            <p className="mb-1">
                              {message.content}
                            </p>
                            <p 
                              className="small mb-0"
                              style={{
                                opacity: 0.8
                              }}
                            >
                              {new Date(message.created_at).toLocaleTimeString([], { 
                                hour: '2-digit', 
                                minute: '2-digit' 
                              })}
                            </p>
                          </div>
                        </div>
                      );
                    })}
                    <div ref={messagesEndRef} />
                  </div>
                </div>
                
                <div className="p-3 border-top">
                  <div className="input-group">
                    <textarea
                      className="form-control"
                      rows="2"
                      placeholder="Type your message..."
                      value={newMessage}
                      onChange={(e) => setNewMessage(e.target.value)}
                      onKeyPress={handleKeyPress}
                      disabled={sending}
                      style={{ resize: 'none' }}
                    ></textarea>
                    <button 
                      className="btn text-white"
                      style={{ backgroundColor: arcaGreen }}
                      onClick={handleSendMessage}
                      disabled={sending || !newMessage.trim()}
                    >
                      {sending ? (
                        <span className="spinner-border spinner-border-sm" role="status">
                          <span className="visually-hidden">Sending...</span>
                        </span>
                      ) : (
                        <i className="fas fa-paper-plane"></i>
                      )}
                      <span className="ms-2 d-none d-sm-inline">{sending ? 'Sending...' : 'Send'}</span>
                    </button>
                  </div>
                </div>
              </>
            ) : (
              <div className="flex-grow-1 d-flex align-items-center justify-content-center">
                <div className="text-center text-muted">
                  <i className="fas fa-comments fa-4x mb-3" style={{ color: '#dee2e6' }}></i>
                  <h5 className="fw-medium">Select a conversation</h5>
                  <p>Choose a conversation from the list to start messaging</p>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default PrivateMessaging;